<template>
  <div v-if="isAuthenticated">
    <div class="relative z-0 flex">
      <div class="relative w-full">
        <input
          ref="inputElement"
          v-model.number="enteredQuantity"
          type="number"
          :disabled="disabled"
          :max="maxQty"
          :min="minQty"
          :class="{
            'z-10 border-[color:var(--color-danger)] focus:border-[color:var(--color-danger-hover)]': !!errorMessage,
          }"
          class="border-gray-300 focus:border-gray-400 -mr-px h-12 w-full min-w-[60px] flex-1 appearance-none rounded-l rounded-r-none border px-1 text-center text-base leading-9 outline-none lg:text-sm"
          @input="onInput"
          @keypress="onKeypress"
          @click="onClick"
          @blur="onBlur"
          @wheel.prevent
        />
      </div>

      <VcButton
        class="h-12 !min-w-[52px] !rounded-l-none"
        color="secondary"
        :loading="loading"
        :disabled="disabled || !!errorMessage"
        size="sm"
        truncate
        @click="onChange"
      >
        <VcIcon class="text-white" name="custom-cart" :size="28" />
      </VcButton>
    </div>

    <!-- Info hint -->
    <VcTooltip v-if="errorMessage" class="!block" :x-offset="28" placement="bottom-start" strategy="fixed">
      <template #trigger>
        <div class="line-clamp-1 pt-0.5 text-xs text-[color:var(--color-danger)]">
          {{ errorMessage }}
        </div>
      </template>

      <template #content>
        <div class="text-tooltip shadow-sm-x-y w-52 rounded-sm bg-white px-3.5 py-1.5 text-xs">
          {{ errorMessage }}
        </div>
      </template>
    </VcTooltip>

    <div v-else-if="reservedSpace" class="h-4"></div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { clone } from "lodash";
import { useField } from "vee-validate";
import { computed, ref, shallowRef } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorsTranslator, useAnalytics } from "@/core/composables";
import { ValidationErrorObjectType } from "@/core/enums";
import { Logger, getDynamicPropertyValue } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { useShortCart } from "@/shared/cart/composables";
import { useNotifications } from "@/shared/notification";
import { useQuantityValidationSchema } from "@/ui-kit/composables";
import type { Product, ShortCartFragment, ShortLineItemFragment, ValidationErrorType } from "@/core/api/graphql/types";
import { useTweakwise } from "@/shared/tweakwise";

const { tweakwiseEvent } = useTweakwise();

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();
const notifications = useNotifications();
const QUANTITY_PROPERTY = "qtyincrements_property";

interface IEmits {
  (event: "update:lineItem", lineItem: ShortLineItemFragment): void;
}

interface IProps {
  product: Product;
  reservedSpace?: boolean;
  large?: boolean;
  hideQuantity?: boolean;
}

const defaultQuantity = computed<number>(
  () => getDynamicPropertyValue(props.product.properties, QUANTITY_PROPERTY) ?? 1,
);

const { isAuthenticated } = useUser();

// Define max qty available to add
const MAX_VALUE = 999999999;

const availableQuantity = computed(() => props.product.availabilityData?.availableQuantity);
const minQuantity = computed(() => props.product.minQuantity);
const maxQuantity = computed(() => props.product.maxQuantity);

const { cart, addToCart, changeItemQuantity } = useShortCart();
const { t } = useI18n();
const { analytics } = useAnalytics();
const { translate } = useErrorsTranslator<ValidationErrorType>("validation_error");
const { quantitySchema } = useQuantityValidationSchema({
  availableQuantity,
  minQuantity,
  maxQuantity,
});

const loading = ref(false);
const inputElement = shallowRef<HTMLInputElement>();

const countInCart = computed<number>(() => getLineItem(cart.value?.items)?.quantity || 0);
const minQty = computed<number>(() => minQuantity.value || 1);
const maxQty = computed<number>(() => {
  const allowBackOrder = props.product.availabilityData?.inventories?.some((item) => item.allowBackorder);
  if (allowBackOrder) {
    return Math.min(MAX_VALUE);
  } else {
    return Math.min(props.product.availabilityData?.availableQuantity || MAX_VALUE, maxQuantity.value || MAX_VALUE);
  }
});

const disabled = computed<boolean>(() => {
  if (!props.product.availabilityData.isBuyable) {
    return true;
  } else if (
    props.product.sapStockStatus == 1 ||
    props.product.sapStockStatus == 2 ||
    props.product.sapStockStatus == 3
  ) {
    return false;
  } else if (props.product.sapStockStatus == 0 || props.product.sapStockStatus == 4) {
    return true;
  } else if (!props.product.availabilityData?.isBuyable || !props.product.availabilityData?.isAvailable) {
    return true;
  } else {
    return (
      loading.value ||
      !props.product.availabilityData?.isAvailable ||
      !props.product.availabilityData?.isInStock ||
      !props.product.availabilityData?.isBuyable ||
      !props.product.availabilityData?.availableQuantity
    );
  }
});

const rules = computed(() => toTypedSchema(quantitySchema.value));

const enteredQuantity = ref(!disabled.value ? countInCart.value || defaultQuantity.value || minQty.value : undefined);

const { errorMessage, validate, setValue } = useField("quantity", rules, {
  initialValue: enteredQuantity,
  validateOnMount: true,
});

/**
 * Process button click to add/update cart line item.
 */
async function onChange() {
  const { valid } = await validate();

  if (!valid || disabled.value) {
    return;
  }

  loading.value = true;

  let lineItem = getLineItem(cart.value?.items);

  let updatedCart: ShortCartFragment | undefined;

  if (enteredQuantity.value && defaultQuantity.value > 1) {
    const batchQuantity = defaultQuantity.value || 1;
    enteredQuantity.value = Math.ceil(enteredQuantity.value / batchQuantity) * batchQuantity;
  }

  const isAlreadyExistsInTheCart = !!lineItem;
  if (isAlreadyExistsInTheCart) {
    updatedCart = await changeItemQuantity(lineItem!.id, enteredQuantity.value || 0);
  } else {
    const inputQuantity = enteredQuantity.value || minQty.value;

    updatedCart = await addToCart(props.product.id!, inputQuantity);

    /**
     * Send Google Analytics event for an item added to cart.
     */
    analytics("addItemToCart", props.product, inputQuantity);

    const batchQtyAddItem = defaultQuantity.value || 1;

    tweakwiseEvent("addtocart", {
      productKey: props.product.code,
      quantity: inputQuantity,
      totalAmount: batchQtyAddItem
        ? props.product.price.actual.amount * (inputQuantity / batchQtyAddItem)
        : props.product.price.actual.amount * inputQuantity,
    });
  }

  lineItem = clone(getLineItem(updatedCart?.items));

  if (!lineItem) {
    Logger.error(onChange.name, 'The variable "lineItem" must be defined');
    notifications.error({
      text: t(
        isAlreadyExistsInTheCart
          ? "common.messages.fail_to_change_quantity_in_cart"
          : "common.messages.fail_add_product_to_cart",
        {
          reason: updatedCart?.validationErrors
            ?.filter(
              (validationError) =>
                validationError.objectId === props.product.id &&
                validationError.objectType === ValidationErrorObjectType.CatalogProduct,
            )
            .map((el) => {
              return translate(el);
            })
            .join(" "),
        },
      ),
      duration: 4000,
      single: true,
    });
  } else {
    emit("update:lineItem", lineItem);
  }

  loading.value = false;
}

function getLineItem(items?: ShortLineItemFragment[]): ShortLineItemFragment | undefined {
  return items?.find((item) => item.productId === props.product.id);
}

/**
 * Ignore non-numeric keys.
 */
function onKeypress(event: KeyboardEvent) {
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}

/**
 * Limit max value.
 */
function onInput() {
  if (!enteredQuantity.value) {
    enteredQuantity.value = undefined;
  } else if (enteredQuantity.value > MAX_VALUE) {
    enteredQuantity.value = MAX_VALUE;
  } else {
    setValue(enteredQuantity.value);
  }
}

function onBlur() {
  if (!enteredQuantity.value || enteredQuantity.value < 1) {
    enteredQuantity.value = countInCart.value || minQty.value;
  }
}

/**
 * Select input value.
 */
function onClick() {
  inputElement.value!.select();
}
</script>
